//usage : < tr-input type="number" cfgOnChange trcad-param="main:numParts" onChange="inputChanged" postConfig="" cooloff="1000"></tr-input>

import { devlog } from "../../util/utils.ts";
import { sendEv } from '../../data/events.ts';

interface TrInputConfig {
  type: string;
  cfgOnChange?: boolean;
  'trcad-param'?: string;
  onChange?: string;
  postConfig?: string;
  cooloff?: number;
}

/**
 * Input component that handles parameter changes and configuration
 * Supports number inputs with optional change handlers and cooldown periods
 */
export class TrInput extends HTMLElement {
  static readonly tag = 'tr-input' as const;

  private _lastTimeout?: number;
  private _inputEl: HTMLInputElement;

  static get observedAttributes(): string[] {
    return ['type', 'cfgOnChange', 'trcad-param', 'onChange', 'postConfig', 'cooloff'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });

    // Create input element
    this._inputEl = document.createElement('input');
    
    // Set up styles
    const style = document.createElement('style');
    style.textContent = `
      input {
        padding: 4px 8px;
        border: 1px solid #ccc;
        border-radius: 4px;
        font-size: 14px;
      }
    `;

    this.shadowRoot?.appendChild(style);
    this.shadowRoot?.appendChild(this._inputEl);

    // Bind event handlers
    this._handleInput = this._handleInput.bind(this);
  }

  connectedCallback(): void {
    const inputType = this.getAttribute('type') ?? 'text';
    this._inputEl.type = inputType;
    this._inputEl.className = `${inputType}`; // Add type-based class
    this._inputEl.addEventListener('input', this._handleInput);
  }

  disconnectedCallback(): void {
    this._inputEl.removeEventListener('input', this._handleInput);
  }

  private _handleInput(e: Event): void {
    const target = e.target as HTMLInputElement;
    const value = target.value;
    const cooloff = parseInt(this.getAttribute('cooloff') ?? '0');
    // devlog("Input event triggered", value);
    // Clear existing timeout
    if (this._lastTimeout) {
      window.clearTimeout(this._lastTimeout);
    }

    // Set new timeout if cooloff specified
    if (cooloff > 0) {
      this._lastTimeout = window.setTimeout(() => {
        this._dispatchChange(value);
      }, cooloff);
    } else {
      this._dispatchChange(value);
    }
  }

  private _dispatchChange(value: string): void {
    // Handle trcad-param binding
    const paramPath = this.getAttribute('trcad-param');
    
    if (paramPath) {
      devlog("Dispatching change event", value, paramPath);
      // Dispatch custom event for parameter change
      const detail = {
        paramPath,
        value: this.getAttribute('type') === 'number' ? parseFloat(value) : value
      };
      sendEv(this, 'as-param-change', { detail });
      //TODO:@REMOVE — deprecated unprefixed name.
      sendEv(this, 'param-change', { detail });
    }
    else {
      console.warn("No trcad-param attribute found on tr-input");
    }

    // Handle onChange callback
    const onChangeHandler = this.getAttribute('onChange');
    if (onChangeHandler) {
      sendEv(this, onChangeHandler, { detail: { value } });
    }
  }
}
// devlog("Registering tr-input")
if (!customElements.get(TrInput.tag)) {
  customElements.define(TrInput.tag, TrInput);
} else {
  console.warn("tr-input already defined");
}
